export interface SeoConfig {
  title: string;
  description: string;
  keywords: string;
  path: string;
  /** Used for og:type and as a hint for JSON-LD type. */
  type: 'website' | 'article' | 'faq';
}

export const SITE_NAME = 'CSSBuy Spreadsheet';
export const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://cssbuybr.lat';
export const SITE_IMAGE =
  'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png';
export const LAST_MODIFIED = '2026-08-30';

const seoMap: Record<string, SeoConfig> = {
  '/': {
    title: 'CSSBuy Spreadsheet 2026 | Finds, Products & Buying Guides',
    description:
      'Explore the CSSBuy Spreadsheet with organized product finds, QC guides, shipping information, reviews, comparisons and practical buying tips.',
    keywords: 'CSSBuy Spreadsheet, CSSBuy Review, CSSBuy Shipping, CSSBuy Coupon, CSSBuy QC, CSSBuy Guide',
    path: '/',
    type: 'website',
  },
  '/about': {
    title: 'About Us | CSSBuy Spreadsheet Resource Hub',
    description:
      'Learn about the CSSBuy Spreadsheet resource hub, an independent informational site providing guides, reviews and buying resources for CSSBuy users.',
    keywords: 'CSSBuy Spreadsheet, about, CSSBuy resource, independent guide',
    path: '/about',
    type: 'website',
  },
  '/contact': {
    title: 'Contact Us | CSSBuy Spreadsheet',
    description:
      'Get in touch with the CSSBuy Spreadsheet team for questions, feedback and suggestions about our independent CSSBuy guides and resources.',
    keywords: 'CSSBuy Spreadsheet, contact, CSSBuy help',
    path: '/contact',
    type: 'website',
  },
  '/disclaimer': {
    title: 'Disclaimer | CSSBuy Spreadsheet',
    description:
      'Read the CSSBuy Spreadsheet disclaimer explaining that this is an independent informational resource and not the official CSSBuy website.',
    keywords: 'CSSBuy Spreadsheet, disclaimer, independent resource',
    path: '/disclaimer',
    type: 'website',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | CSSBuy Spreadsheet',
    description:
      'Read the privacy policy for CSSBuy Spreadsheet, explaining how we handle information on this independent CSSBuy resource site.',
    keywords: 'CSSBuy Spreadsheet, privacy policy',
    path: '/privacy-policy',
    type: 'website',
  },
  '/terms': {
    title: 'Terms of Use | CSSBuy Spreadsheet',
    description:
      'Read the terms of use for CSSBuy Spreadsheet, an independent informational resource about CSSBuy guides and spreadsheets.',
    keywords: 'CSSBuy Spreadsheet, terms of use',
    path: '/terms',
    type: 'website',
  },
  '/safety': {
    title: 'CSSBuy Safety Guide | Stay Safe When Buying',
    description:
      'Learn essential CSSBuy safety tips including seller verification, QC checks, safe payments, customs rules and scam prevention.',
    keywords: 'CSSBuy safety, CSSBuy scam prevention, CSSBuy seller verification, CSSBuy Spreadsheet',
    path: '/safety',
    type: 'article',
  },
  '/faq': {
    title: 'CSSBuy Spreadsheet FAQ | Frequently Asked Questions',
    description:
      'Find answers to frequently asked questions about the CSSBuy Spreadsheet, CSSBuy fees, shipping, QC, warehouse and safety.',
    keywords: 'CSSBuy FAQ, CSSBuy Spreadsheet, CSSBuy questions',
    path: '/faq',
    type: 'faq',
  },
  '/guides': {
    title: 'CSSBuy Guides | Spreadsheet, Buying & Shipping Guides',
    description:
      'Browse all CSSBuy guides covering spreadsheet usage, buying, shipping, warehouse, QC, coupons and safety.',
    keywords: 'CSSBuy guides, CSSBuy Spreadsheet, CSSBuy buying guide',
    path: '/guides',
    type: 'website',
  },
  '/reviews': {
    title: 'CSSBuy Reviews | Independent Reviews & Ratings',
    description:
      'Read independent CSSBuy reviews covering fees, quality control, shipping, warehouse and overall user experience.',
    keywords: 'CSSBuy reviews, CSSBuy review 2026, CSSBuy Spreadsheet',
    path: '/reviews',
    type: 'website',
  },
  '/reviews/cssbuy-review': {
    title: 'CSSBuy Review 2026: Fees, QC, Shipping & Warehouse',
    description:
      'A comprehensive independent review of CSSBuy covering fees, quality control, shipping options, warehouse features, and the overall buying experience.',
    keywords: 'CSSBuy Review, CSSBuy fees, CSSBuy QC, CSSBuy warehouse, CSSBuy Spreadsheet',
    path: '/reviews/cssbuy-review',
    type: 'article',
  },
  '/reviews/is-cssbuy-legit': {
    title: 'Is CSSBuy Legit? | Safety & Scam Prevention Guide',
    description:
      'Find out whether CSSBuy is legit, how to verify sellers, avoid scams, and shop safely using the CSSBuy Spreadsheet.',
    keywords: 'CSSBuy legit, CSSBuy safe, CSSBuy scam, CSSBuy Spreadsheet',
    path: '/reviews/is-cssbuy-legit',
    type: 'article',
  },
  '/shipping': {
    title: 'CSSBuy Shipping | Costs, Lines & Times',
    description:
      'Understand CSSBuy shipping costs, lines, delivery times and tips for international shipping.',
    keywords: 'CSSBuy shipping, CSSBuy shipping cost, CSSBuy Spreadsheet',
    path: '/shipping',
    type: 'website',
  },
  '/shipping/cssbuy-shipping-cost': {
    title: 'CSSBuy Shipping Cost 2026: Complete Guide',
    description:
      'A complete guide to CSSBuy shipping costs covering weight, volume, destination, shipping lines, packaging and customs.',
    keywords: 'CSSBuy shipping cost, CSSBuy shipping lines, CSSBuy Spreadsheet',
    path: '/shipping/cssbuy-shipping-cost',
    type: 'article',
  },
  '/warehouse': {
    title: 'CSSBuy Warehouse | Storage, QC & Consolidation',
    description:
      'Learn how the CSSBuy warehouse works including storage, quality control photos, consolidation and shipping.',
    keywords: 'CSSBuy warehouse, CSSBuy storage, CSSBuy Spreadsheet',
    path: '/warehouse',
    type: 'website',
  },
  '/warehouse/cssbuy-warehouse': {
    title: 'CSSBuy Warehouse Guide: Storage, QC & Process',
    description:
      'A complete guide to the CSSBuy warehouse covering the order to shipping process, QC photos, storage and consolidation.',
    keywords: 'CSSBuy warehouse, CSSBuy QC, CSSBuy Spreadsheet',
    path: '/warehouse/cssbuy-warehouse',
    type: 'article',
  },
  '/qc': {
    title: 'CSSBuy QC | Quality Control & Inspection',
    description:
      'Learn about CSSBuy quality control, QC photos, product inspection and how to approve your items before shipping.',
    keywords: 'CSSBuy QC, CSSBuy quality control, CSSBuy Spreadsheet',
    path: '/qc',
    type: 'website',
  },
  '/qc/cssbuy-qc-guide': {
    title: 'CSSBuy QC Guide: Quality Control Photos & Inspection',
    description:
      'A complete CSSBuy QC guide covering quality control photos, product inspection and how to spot issues before shipping.',
    keywords: 'CSSBuy QC, CSSBuy QC photos, CSSBuy Spreadsheet',
    path: '/qc/cssbuy-qc-guide',
    type: 'article',
  },
  '/coupon': {
    title: 'CSSBuy Coupon | Discount & Promo Codes',
    description:
      'Find information about CSSBuy coupons, discount codes and promotions to save on your purchases.',
    keywords: 'CSSBuy coupon, CSSBuy discount code, CSSBuy promo, CSSBuy Spreadsheet',
    path: '/coupon',
    type: 'website',
  },
  '/coupon/cssbuy-coupon': {
    title: 'CSSBuy Coupon & Discount Code Guide 2026',
    description:
      'A guide to CSSBuy coupons and discount codes, including how to check current promotions and verified deals.',
    keywords: 'CSSBuy coupon, CSSBuy discount code, CSSBuy promo code, CSSBuy Spreadsheet',
    path: '/coupon/cssbuy-coupon',
    type: 'article',
  },
  '/comparison': {
    title: 'CSSBuy vs Other Agents | Comparison Guides',
    description:
      'Compare CSSBuy with other buying agents including Superbuy, Sugargoo and alternatives to find the best option.',
    keywords: 'CSSBuy comparison, CSSBuy vs Superbuy, CSSBuy alternatives',
    path: '/comparison',
    type: 'website',
  },
  '/spreadsheet': {
    title: 'CSSBuy Spreadsheet Hub | Products, Finds & Categories',
    description:
      'Explore the CSSBuy Spreadsheet hub with organized products, categories, finds and links for smarter buying.',
    keywords: 'CSSBuy Spreadsheet, CSSBuy finds, CSSBuy products',
    path: '/spreadsheet',
    type: 'website',
  },
  '/spreadsheet/cssbuy-spreadsheet-complete-guide': {
    title: 'CSSBuy Spreadsheet 2026: Products, Finds & Complete Guide',
    description:
      'The complete CSSBuy Spreadsheet guide covering how it works, how to search, find products, check links and sellers.',
    keywords: 'CSSBuy Spreadsheet, CSSBuy complete guide, CSSBuy finds',
    path: '/spreadsheet/cssbuy-spreadsheet-complete-guide',
    type: 'article',
  },
  '/guides/how-to-use-cssbuy-spreadsheet': {
    title: 'How to Use CSSBuy Spreadsheet | Step-by-Step Guide',
    description:
      'A step-by-step guide on how to use the CSSBuy Spreadsheet to search, compare and verify products before buying.',
    keywords: 'CSSBuy Spreadsheet, how to use CSSBuy, CSSBuy guide',
    path: '/guides/how-to-use-cssbuy-spreadsheet',
    type: 'article',
  },
  '/guides/cssbuy-spreadsheet-beginner-guide': {
    title: 'CSSBuy Spreadsheet Beginner Guide | Get Started',
    description:
      'A beginner-friendly guide to the CSSBuy Spreadsheet covering the basics every new buyer needs to know.',
    keywords: 'CSSBuy Spreadsheet, CSSBuy beginner, CSSBuy guide',
    path: '/guides/cssbuy-spreadsheet-beginner-guide',
    type: 'article',
  },
  '/spreadsheet/cssbuy-shoes-spreadsheet': {
    title: 'CSSBuy Shoes Spreadsheet | Finds & Buying Guide',
    description:
      'Discover the CSSBuy shoes spreadsheet with popular shoes, categories and tips for finding the right pair.',
    keywords: 'CSSBuy shoes, CSSBuy shoes spreadsheet, CSSBuy finds',
    path: '/spreadsheet/cssbuy-shoes-spreadsheet',
    type: 'article',
  },
  '/spreadsheet/cssbuy-sneakers-spreadsheet': {
    title: 'CSSBuy Sneakers Spreadsheet | Finds & QC Tips',
    description:
      'Explore the CSSBuy sneakers spreadsheet with sneaker finds, brands and QC tips for sneaker buyers.',
    keywords: 'CSSBuy sneakers, CSSBuy sneakers spreadsheet, CSSBuy finds',
    path: '/spreadsheet/cssbuy-sneakers-spreadsheet',
    type: 'article',
  },
  '/spreadsheet/cssbuy-hoodies-spreadsheet': {
    title: 'CSSBuy Hoodies Spreadsheet | Finds & Sellers',
    description:
      'Browse the CSSBuy hoodies spreadsheet with hoodie finds, sellers and buying advice.',
    keywords: 'CSSBuy hoodies, CSSBuy hoodies spreadsheet, CSSBuy finds',
    path: '/spreadsheet/cssbuy-hoodies-spreadsheet',
    type: 'article',
  },
  '/guides/cssbuy-taobao-guide': {
    title: 'CSSBuy Taobao Guide | How to Buy from Taobao',
    description:
      'A complete guide to buying from Taobao through CSSBuy, covering browsing, product links and ordering.',
    keywords: 'CSSBuy Taobao, CSSBuy Taobao guide, CSSBuy Spreadsheet',
    path: '/guides/cssbuy-taobao-guide',
    type: 'article',
  },
  '/guides/cssbuy-weidian-guide': {
    title: 'CSSBuy Weidian Guide | Shopping & Seller Verification',
    description:
      'Learn how to shop on Weidian through CSSBuy including seller verification and product links.',
    keywords: 'CSSBuy Weidian, CSSBuy Weidian guide, CSSBuy Spreadsheet',
    path: '/guides/cssbuy-weidian-guide',
    type: 'article',
  },
  '/guides/cssbuy-yupoo-guide': {
    title: 'CSSBuy Yupoo Guide | Catalogs & Product Links',
    description: 'Understand Yupoo catalogs and product links when buying through CSSBuy.',
    keywords: 'CSSBuy Yupoo, CSSBuy Yupoo guide, CSSBuy Spreadsheet',
    path: '/guides/cssbuy-yupoo-guide',
    type: 'article',
  },
  '/comparison/cssbuy-vs-superbuy': {
    title: 'CSSBuy vs Superbuy | Full Comparison 2026',
    description:
      'A feature-by-feature comparison of CSSBuy and Superbuy covering fees, shipping, QC and warehouse.',
    keywords: 'CSSBuy vs Superbuy, CSSBuy comparison, CSSBuy Spreadsheet',
    path: '/comparison/cssbuy-vs-superbuy',
    type: 'article',
  },
  '/comparison/cssbuy-alternatives': {
    title: 'Best CSSBuy Alternatives | Buying Agents Compared',
    description:
      'Explore the best CSSBuy alternatives and other buying agents to find the right option for your needs.',
    keywords: 'CSSBuy alternatives, best CSSBuy alternatives, CSSBuy Spreadsheet',
    path: '/comparison/cssbuy-alternatives',
    type: 'article',
  },
  '/categories': {
    title: 'CSSBuy Spreadsheet Categories | Browse All',
    description:
      'Browse all CSSBuy Spreadsheet categories including shoes, hoodies, t-shirts, jackets, bags and accessories.',
    keywords: 'CSSBuy categories, CSSBuy Spreadsheet, CSSBuy products',
    path: '/categories',
    type: 'website',
  },
};

const fallback: SeoConfig = {
  title: 'CSSBuy Spreadsheet 2026 | Finds, Products & Buying Guides',
  description:
    'Explore the CSSBuy Spreadsheet with organized product finds, QC guides, shipping information, reviews, comparisons and practical buying tips.',
  keywords: 'CSSBuy Spreadsheet, CSSBuy Review, CSSBuy Shipping, CSSBuy Coupon, CSSBuy QC, CSSBuy Guide',
  path: '/',
  type: 'website',
};

export function getSeo(pathname: string): SeoConfig {
  return seoMap[pathname] || fallback;
}

export function getAllSeoPaths(): string[] {
  return Object.keys(seoMap);
}